import { formatCurrencyUY } from "../utils/formatters";
import { friendlyBreakdownName, groupFundsByBankAccount } from "../utils/fundHelpers";
import { loadImageForPDF } from "../utils/pdfAssets";
import { detalleCorto } from "../utils/detalleGasto";
import logoImg from "../assets/logo.png";

// Abreviatura corta para mostrar en la columna "Desglose" del PDF.
// Los fondos built-in mantienen sus siglas (GC/FR); los fondos custom usan el nombre corto del edificio.
const shortBreakdownLabel = (key, building) => {
  if (key === "gastos_comunes") return "GC";
  if (key === "fondo_reserva") return "FR";
  if (key === "multas") return "Multas";
  if (key === "reajuste") return "Reajuste";
  if (key === "intereses") return "Recargo mora";
  // Fondos custom: usar el nombre definido por el admin en customFunds.
  return friendlyBreakdownName(key, building);
};

// Estilos compactos compartidos por todas las tablas del PDF (menos hojas).
const TABLE_HEAD_STYLES = {
  fillColor: [0, 128, 255],
  textColor: [255, 255, 255],
  fontStyle: "bold",
  fontSize: 8,
  cellPadding: 1.6,
};
const TABLE_BODY_STYLES = {
  fontSize: 8,
  cellPadding: 1.6,
};

/**
 * Genera un PDF de liquidacion de gastos comunes para un cierre de mes.
 * @param {Object} params
 * @param {Object} params.building - datos del edificio
 * @param {number} params.month - mes del cierre
 * @param {number} params.year - ano del cierre
 * @param {Array} params.expenses - gastos del mes [{description, amount, invoiceDate, supplierName}]
 * @param {Array} params.apartments - unidades [{number, owner, tenant, area}]
 * @param {Array} params.payments - pagos generados [{userid, amount, description, unitNumber, userName}]
 * @param {string} params.maturity - fecha de vencimiento
 * @param {string} params.adminName - nombre del administrador
 * @param {Object} [params.fundBalances] - resumen por fondo UYU de calculateFundBalances
 * @param {Object} [params.fundBalancesUSD] - idem en dolares (se imprime solo si tiene movimientos)
 * @param {"download"|"blob"} [params.output] - "download" (default) baja el archivo;
 *   "blob" lo devuelve como { blob, fileName } (lo usa la aprobación del cierre
 *   para subirlo a Storage y mandarlo por WhatsApp a los residentes).
 */
export const generarLiquidacionPDF = async ({
  building,
  month,
  year,
  expenses = [],
  apartments = [],
  payments = [],
  maturity = "",
  adminName = "Administrador",
  fundBalances = null,
  fundBalancesUSD = null,
  output = "download",
}) => {
  // Lazy load jsPDF y autoTable para reducir bundle (~250KB gz)
  const { default: jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const mg = 14;

  const meses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];
  const mesNombre = meses[month - 1] || "";

  // === HEADER: logo Habitia + titulo + periodo (compacto, sin banda) ===
  let y = 12;
  let logoH = 0;
  try {
    const logoResult = await loadImageForPDF(logoImg, 300);
    if (logoResult) {
      const logoW = 34;
      logoH = logoW / logoResult.ratio; // logo.png 3369x1493 → ~15mm
      doc.addImage(logoResult.data, "PNG", mg, y, logoW, logoH);
    }
  } catch (_) { /* sin logo no se frena la liquidacion */ }

  doc.setTextColor(17, 24, 39);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Liquidacion de Gastos Comunes", pageWidth - mg, y + 5, { align: "right" });
  doc.setFontSize(11);
  doc.text(`${mesNombre} ${year}`, pageWidth - mg, y + 11, { align: "right" });
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(156, 163, 175);
  doc.text(`Generado: ${new Date().toLocaleDateString("es-UY")}`, pageWidth - mg, y + 16, { align: "right" });

  y += Math.max(logoH, 16) + 4;

  // Linea de acento (trazo azul corto + resto gris, mismo patron que Estado de Cuenta)
  doc.setDrawColor(0, 128, 255);
  doc.setLineWidth(0.8);
  doc.line(mg, y, mg + 30, y);
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.2);
  doc.line(mg + 30, y, pageWidth - mg, y);
  y += 6;

  // === DATOS DEL EDIFICIO (2 lineas compactas, sin seccion aparte) ===
  doc.setFontSize(9);
  doc.setTextColor(55, 65, 81);
  doc.setFont("helvetica", "bold");
  doc.text(`${building?.name || building?.nombre || "Sin nombre"}`, mg, y);
  doc.setFont("helvetica", "normal");
  const dir = [building?.address, building?.city].filter(Boolean).join(", ");
  if (dir) {
    const nameW = doc.getTextWidth(`${building?.name || building?.nombre || "Sin nombre"}`);
    doc.setTextColor(107, 114, 128);
    doc.text(`  ·  ${dir}`, mg + nameW, y);
  }
  y += 5;
  doc.setTextColor(107, 114, 128);
  const infoParts = [
    `Administrador: ${adminName}`,
    maturity ? `Vencimiento: ${maturity}` : null,
    `Unidades: ${apartments.length}`,
  ].filter(Boolean);
  doc.text(infoParts.join("   ·   "), mg, y);
  y += 8;

  const sectionTitle = (title) => {
    if (y > 250) {
      doc.addPage();
      y = 16;
    }
    doc.setFontSize(11);
    doc.setTextColor(17, 24, 39);
    doc.setFont("helvetica", "bold");
    doc.text(title, mg, y);
    y += 3;
  };

  // === RESUMEN POR FONDOS ===
  // Saldo anterior + cobranza + gastos + creditos (ingresos del edificio) +
  // debitos + saldo actual, por fondo (caja, reserva y fondos custom activos).
  const fmtUSD = (v) =>
    `US$ ${Number(v || 0).toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const renderFundsTable = (funds, title, formatFn) => {
    sectionTitle(title);
    const rows = [
      ["Caja", funds.caja],
      ["Reserva", funds.reserva],
    ];
    (building?.customFunds || [])
      .filter((f) => f.active !== false)
      .forEach((f) => {
        if (funds[f.id]) rows.push([f.name || f.id, funds[f.id]]);
      });

    const body = rows.map(([label, d]) => [
      label,
      formatFn(d.saldoAnterior),
      formatFn(d.cobranza),
      formatFn(d.gastos),
      formatFn(d.creditos),
      formatFn(d.debitos),
      formatFn(d.saldoActual),
    ]);
    body.push([
      { content: "TOTAL", styles: { fontStyle: "bold" } },
      { content: formatFn(funds.total.saldoAnterior), styles: { fontStyle: "bold" } },
      { content: formatFn(funds.total.cobranza), styles: { fontStyle: "bold" } },
      { content: formatFn(funds.total.gastos), styles: { fontStyle: "bold" } },
      { content: formatFn(funds.total.creditos), styles: { fontStyle: "bold" } },
      { content: formatFn(funds.total.debitos), styles: { fontStyle: "bold" } },
      { content: formatFn(funds.total.saldoActual), styles: { fontStyle: "bold" } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Fondo", "Saldo anterior", "Cobranza", "Gastos", "Creditos", "Debitos", "Saldo actual"]],
      body,
      theme: "grid",
      headStyles: TABLE_HEAD_STYLES,
      bodyStyles: TABLE_BODY_STYLES,
      columnStyles: {
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
      },
      margin: { left: mg, right: mg },
    });
    y = doc.lastAutoTable.finalY + 7;
  };

  if (fundBalances) {
    renderFundsTable(fundBalances, "Resumen por Fondos", formatCurrencyUY);
    const t = fundBalancesUSD?.total;
    const hasUSD = t && (t.cobranza > 0 || t.gastos > 0 || t.saldoAnterior !== 0 || t.creditos > 0);
    if (hasUSD) {
      renderFundsTable(fundBalancesUSD, "Resumen por Fondos (USD)", fmtUSD);
    }
  }

  // === DETALLE DE GASTOS ===
  sectionTitle("Detalle de Gastos del Mes");

  const totalGastos = expenses.reduce((sum, e) => sum + Number(e.amount || 0), 0);

  if (expenses.length > 0) {
    // Resuelve el nombre del fondo que paga un gasto, respetando sourceFund y con
    // fallback legacy (obra → Fondo Reserva, resto → Caja/Gastos Comunes).
    const resolveFundName = (expense) => {
      const src = expense.sourceFund;
      if (src === "caja") return "Caja";
      if (src === "reserva") return "Fondo Reserva";
      if (src) {
        const cf = (building?.customFunds || []).find(f => f.id === src);
        return cf?.name || src;
      }
      const cat = expense.expenseCategory || "gastos_comunes";
      return cat === "obra" ? "Fondo Reserva" : "Caja";
    };

    const gastosData = expenses.map((e) => {
      let fechaStr = "";
      if (e.invoiceDate) {
        if (typeof e.invoiceDate === "string") {
          fechaStr = e.invoiceDate;
        } else if (e.invoiceDate?.seconds) {
          fechaStr = new Date(e.invoiceDate.seconds * 1000).toLocaleDateString("es-UY");
        } else if (e.invoiceDate?.toDate) {
          fechaStr = e.invoiceDate.toDate().toLocaleDateString("es-UY");
        }
      }
      return [
        detalleCorto(e) || "Sin descripcion",
        e.supplierName || "-",
        fechaStr,
        resolveFundName(e),
        formatCurrencyUY(e.amount),
      ];
    });

    // Fila de total
    gastosData.push([
      { content: "TOTAL GASTOS", colSpan: 4, styles: { fontStyle: "bold", halign: "right" } },
      { content: formatCurrencyUY(totalGastos), styles: { fontStyle: "bold" } },
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Descripcion", "Proveedor", "Fecha Factura", "Fondo", "Monto"]],
      body: gastosData,
      theme: "grid",
      headStyles: TABLE_HEAD_STYLES,
      bodyStyles: TABLE_BODY_STYLES,
      columnStyles: {
        4: { halign: "right" },
      },
      margin: { left: mg, right: mg },
    });

    y = doc.lastAutoTable.finalY + 7;
  } else {
    y += 4;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(107, 114, 128);
    doc.text("No se registraron gastos en este periodo.", mg, y);
    y += 8;
  }

  // === DISTRIBUCION POR UNIDAD ===
  if (payments.length > 0) {
    sectionTitle("Distribucion por Unidad");

    // Desglose en COLUMNAS: una por concepto. Para que entre en el ancho de la
    // hoja, los itemizados se agrupan (todas las multa__* en "Multas", cargo__*
    // en "Cargos", amenity__* en "Amenities", intereses+reajuste en "Rec. mora")
    // y cada fondo custom tiene su propia columna. Solo se imprimen las
    // columnas con algún valor > 0 en el período.
    const customFundKeys = []; // fondo_<id>, orden de primera aparición
    for (const p of payments) {
      for (const key of Object.keys(p.breakdown || {})) {
        if (key.startsWith("fondo_") && key !== "fondo_reserva" && !customFundKeys.includes(key)) {
          customFundKeys.push(key);
        }
      }
    }

    // Valores agregados por concepto para un payment.
    const conceptValues = (p) => {
      const b = p.breakdown || {};
      const sumPrefix = (prefix) =>
        Object.entries(b).reduce(
          (s, [k, v]) => (k.startsWith(prefix) ? s + (Number(v) || 0) : s),
          0
        );
      const out = {
        gastos_comunes: Number(b.gastos_comunes) || 0,
        fondo_reserva: Number(b.fondo_reserva) || 0,
        multas: (Number(b.multas) || 0) + sumPrefix("multa__"),
        cargos: sumPrefix("cargo__"),
        amenities: sumPrefix("amenity__"),
        mora: (Number(b.intereses) || 0) + (Number(b.reajuste) || 0),
      };
      customFundKeys.forEach((k) => {
        out[k] = Number(b[k]) || 0;
      });
      return out;
    };

    // Columnas candidatas en orden; se filtran por total > 0.
    const candidateCols = [
      { key: "gastos_comunes", label: "GC" },
      { key: "fondo_reserva", label: "F. Reserva" },
      ...customFundKeys.map((k) => ({
        key: k,
        label: String(shortBreakdownLabel(k, building)).slice(0, 14),
      })),
      { key: "multas", label: "Multas" },
      { key: "cargos", label: "Cargos" },
      { key: "amenities", label: "Amenities" },
      { key: "mora", label: "Rec. mora" },
    ];

    const perPayment = payments.map((p) => conceptValues(p));
    const colTotals = {};
    candidateCols.forEach((c) => {
      colTotals[c.key] = perPayment.reduce((s, v) => s + (v[c.key] || 0), 0);
    });
    const activeCols = candidateCols.filter((c) => colTotals[c.key] > 0);

    let totalGeneral = 0;
    const paymentsData = payments.map((p, i) => {
      const unitLabel = p._hasBothRoles
        ? `${p.unitNumber || "-"} ${p._role}`
        : `${p.unitNumber || "-"} P`;
      totalGeneral += Number(p.amount) || 0;
      return [
        unitLabel,
        p.userName || "-",
        // Celda vacía cuando el concepto es 0: la tabla se lee de un vistazo.
        ...activeCols.map((c) => (perPayment[i][c.key] > 0 ? formatCurrencyUY(perPayment[i][c.key]) : "")),
        formatCurrencyUY(p.amount),
      ];
    });

    paymentsData.push([
      { content: "TOTAL", colSpan: 2, styles: { fontStyle: "bold", halign: "right" } },
      ...activeCols.map((c) => ({
        content: formatCurrencyUY(colTotals[c.key]),
        styles: { fontStyle: "bold" },
      })),
      { content: formatCurrencyUY(totalGeneral), styles: { fontStyle: "bold" } },
    ]);

    // Alinear a la derecha todas las columnas numéricas (conceptos + Monto).
    const numericColumnStyles = {};
    for (let c = 2; c < 2 + activeCols.length + 1; c++) {
      numericColumnStyles[c] = { halign: "right" };
    }

    autoTable(doc, {
      startY: y,
      head: [["Unidad", "Residente", ...activeCols.map((c) => c.label), "Monto"]],
      body: paymentsData,
      theme: "grid",
      headStyles: { ...TABLE_HEAD_STYLES, halign: "center" },
      // Con muchas columnas de conceptos, bajar un punto la fuente para que entre.
      bodyStyles: { ...TABLE_BODY_STYLES, fontSize: activeCols.length > 5 ? 7 : 8 },
      columnStyles: {
        0: { cellWidth: 14 },
        1: { cellWidth: 30 },
        ...numericColumnStyles,
      },
      margin: { left: mg, right: mg },
    });

    y = doc.lastAutoTable.finalY + 7;
  }

  // === FORMAS DE PAGO ===
  // Cuentas bancarias del edificio (con qué conceptos/fondos se pagan en cada
  // una, si el admin mapeó fondo→cuenta) + métodos de pago disponibles.
  const bankAccounts = Array.isArray(building?.bankAccounts) ? building.bankAccounts : [];
  const linkPago = building?.linkPago || building?.paymentLink || "";
  if (bankAccounts.length > 0 || linkPago) {
    sectionTitle("Formas de Pago");

    if (bankAccounts.length > 0) {
      const { groups } = groupFundsByBankAccount(building);
      const fundNamesByAccount = new Map(
        groups.map((g) => [g.accountId, g.funds.map((f) => f.name).join(", ")])
      );
      const hasMapping = groups.length > 0;

      const head = hasMapping
        ? [["Banco", "Cuenta", "Nº de cuenta", "Moneda", "Ahí se paga"]]
        : [["Banco", "Cuenta", "Nº de cuenta", "Moneda"]];
      const body = bankAccounts.map((acc) => {
        const row = [
          acc.bank || "-",
          acc.accountType || "-",
          acc.accountNumber || "-",
          acc.currency || "UYU",
        ];
        if (hasMapping) row.push(fundNamesByAccount.get(acc.id) || "—");
        return row;
      });

      autoTable(doc, {
        startY: y,
        head,
        body,
        theme: "grid",
        headStyles: TABLE_HEAD_STYLES,
        bodyStyles: TABLE_BODY_STYLES,
        margin: { left: mg, right: mg },
      });
      y = doc.lastAutoTable.finalY + 5;
    }

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99);
    const metodoLines = [];
    if (bankAccounts.length > 0) {
      metodoLines.push(
        "Transferencia bancaria a las cuentas indicadas. Subí el comprobante desde la app (Gastos Comunes) o envialo por WhatsApp a Habi."
      );
    }
    if (linkPago) {
      metodoLines.push(`Pago online: ${linkPago}`);
    }
    metodoLines.forEach((line) => {
      if (y > 265) {
        doc.addPage();
        y = 16;
      }
      const wrapped = doc.splitTextToSize(`• ${line}`, pageWidth - mg * 2);
      doc.text(wrapped, mg, y);
      y += wrapped.length * 4 + 1;
    });
    y += 4;
  }

  // === FOOTER ===
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(mg, pageHeight - 16, pageWidth - mg, pageHeight - 16);

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Liquidacion generada por Habitia - ${building?.name || ""} - ${mesNombre} ${year}`,
      mg,
      pageHeight - 11
    );
    doc.text(
      `Pagina ${i} de ${totalPages}`,
      pageWidth - mg,
      pageHeight - 11,
      { align: "right" }
    );
  }

  const fileName = `Liquidacion_${building?.name || "Edificio"}_${mesNombre}_${year}.pdf`.replace(/\s+/g, "_");
  if (output === "blob") {
    return { blob: doc.output("blob"), fileName };
  }
  // Descargar
  doc.save(fileName);
  return { fileName };
};
